import { ComponentProps, useMemo } from "react";
import { TabPanel, TabView } from "primereact/tabview";
import * as client from "../../../client";
import {
  CatJobHistoryItem,
  CatResultsResponse,
  WorkStatus,
} from "../../../client/types.gen";
import { AalDriversTab } from "./AalDriversTab";
import { EpCurvesTab } from "./EpCurvesTab";
import { LayerPerilComparison, LayerPerilTab } from "./LayerPerilTab";
import { RunHistoryTab } from "./RunHistoryTab";
import { RunPicker } from "./RunPicker";
import { EnrichmentSource, SelectedModel } from "./types";
import { useCatBatchResults } from "./useCatBatchResults";
import styles from "./CatCard.module.css";

type CatResultsViewProps = {
  // On phones, render just the AAL-by-layer/peril table; on desktop, the full
  // tabbed results (EP Curves, AAL Drivers, Run History).
  isMobile: boolean;
  selectedModel: SelectedModel;
  // What each vendor's tab is showing. Handed to the leaf tabs one slot at a
  // time so a change on one vendor doesn't invalidate the others' memos.
  resultsByVendor: Record<SelectedModel, CatResultsResponse | null>;
  layerInfoById: Map<string, client.Layer>;
  showAllLayers: boolean;
  onShowAllLayersChange: (value: boolean) => void;
  topLocationsByPeril:
    | ComponentProps<typeof AalDriversTab>["topLocationsByPeril"]
    | undefined;
  reportId: string;
  jobHistory: CatJobHistoryItem[];
  // The history batch the selected model's tabs show; null for its active run.
  viewedBatchId: string | null;
  // That run's history row; null when the history has no completed row for it.
  viewedJob: CatJobHistoryItem | null;
  // Further completed batches set beside the viewed one on the By Layer and
  // Peril tab.
  compareBatchIds: string[];
  onCompareBatchIdsChange: (batchIds: string[]) => void;
  // The vendor data the shown run modeled with; undefined while unknown.
  enrichmentSource: EnrichmentSource | undefined;
  onViewJobResults: (job: CatJobHistoryItem) => void;
  onDownloadAll: (job: CatJobHistoryItem) => void;
  onCompareJob: ComponentProps<typeof RunHistoryTab>["onCompareJob"];
  eltRecovery: ComponentProps<typeof RunHistoryTab>["eltRecovery"];
  activation: ComponentProps<typeof RunHistoryTab>["activation"];
};

export const CatResultsView = ({
  isMobile,
  selectedModel,
  resultsByVendor,
  layerInfoById,
  showAllLayers,
  onShowAllLayersChange,
  topLocationsByPeril,
  reportId,
  jobHistory,
  viewedBatchId,
  viewedJob,
  compareBatchIds,
  onCompareBatchIdsChange,
  enrichmentSource,
  onViewJobResults,
  onDownloadAll,
  onCompareJob,
  eltRecovery,
  activation,
}: CatResultsViewProps) => {
  const completedJobs = useMemo(
    () =>
      jobHistory.filter(
        (job) => job.type === selectedModel && job.status === WorkStatus.DONE
      ),
    [jobHistory, selectedModel]
  );

  // Compared batches must still be completed runs of this model other than the
  // viewed one: a history refresh or a change of viewed run can orphan a pick.
  const compareJobs = useMemo(
    () =>
      compareBatchIds.flatMap((batchId) => {
        const job = completedJobs.find((j) => j.batch_id === batchId);
        return job && job.batch_id !== viewedJob?.batch_id ? [job] : [];
      }),
    [compareBatchIds, completedJobs, viewedJob]
  );
  const runs = useCatBatchResults(
    reportId,
    compareJobs.map((job) => job.batch_id)
  );
  const comparison: LayerPerilComparison = {
    viewedJob,
    runs: compareJobs.map((job, index) => ({
      job,
      results: runs[index]?.results ?? null,
      status: runs[index]?.status ?? "loading",
    })),
  };

  const layerPerilTab = (renderToggle: boolean) => (
    <LayerPerilTab
      selectedModel={selectedModel}
      resultsData={resultsByVendor[selectedModel]}
      layerInfoById={layerInfoById}
      showAllLayers={showAllLayers}
      onShowAllLayersChange={onShowAllLayersChange}
      renderToggle={renderToggle}
      enrichmentSource={enrichmentSource}
      toolbarControls={
        completedJobs.length > 0 ? (
          <RunPicker
            jobs={completedJobs}
            viewedJob={viewedJob}
            emptyLabel={
              viewedBatchId === null ? "Active results" : "Historical run"
            }
            onView={onViewJobResults}
            compareBatchIds={compareJobs.map((job) => job.batch_id)}
            onCompareChange={onCompareBatchIdsChange}
          />
        ) : undefined
      }
      comparison={comparison}
    />
  );

  return (
    <div className={styles.resultsContainer}>
      {isMobile ? (
        layerPerilTab(false)
      ) : (
        <TabView className={styles.resultsTabs}>
          <TabPanel header="By Layer and Peril">{layerPerilTab(true)}</TabPanel>

          {selectedModel === "spatialkat" && (
            <TabPanel header="EP Curves">
              <EpCurvesTab
                selectedModel={selectedModel}
                spatialKatResultsData={resultsByVendor.spatialkat}
              />
            </TabPanel>
          )}

          {(selectedModel === "rms_elt" || selectedModel === "icm_ylt") &&
            topLocationsByPeril &&
            Object.keys(topLocationsByPeril).length > 0 && (
              <TabPanel header="AAL Drivers">
                <AalDriversTab
                  topLocationsByPeril={topLocationsByPeril}
                  reportId={reportId}
                />
              </TabPanel>
            )}

          <TabPanel header="Run History">
            <RunHistoryTab
              jobHistory={jobHistory}
              selectedModel={selectedModel}
              onDownloadAll={onDownloadAll}
              onCompareJob={onCompareJob}
              eltRecovery={eltRecovery}
              activation={activation}
            />
          </TabPanel>
        </TabView>
      )}
    </div>
  );
};
